import { useState, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils/cn';
import { QuestionWrapper } from './question-wrapper';
import type { QuestionComponentProps, FileUploadAnswer } from '@/types';

/**
 * File upload configuration interface
 */
interface FileUploadDisplayConfig {
  uploadLayout?: 'dropzone' | 'button' | 'inline' | 'compact';
  dropzoneText?: string;
  buttonText?: string;
  showPreview?: boolean;
  previewSize?: 'small' | 'medium' | 'large';
  showFileSize?: boolean;
  showFileType?: boolean;
  showProgress?: boolean;
  showFileCount?: boolean;
  uploadTheme?: 'default' | 'minimal' | 'bordered';
  allowedTypes?: string[];
  maxFileSizeMb?: number;
  maxFiles?: number;
  minFiles?: number;
}

// Preview size values
const PREVIEW_SIZE_VALUES = {
  small: 'h-10 w-10',
  medium: 'h-16 w-16',
  large: 'h-24 w-24',
} as const;

/**
 * File upload question component.
 * Supports single or multiple file uploads with type and size restrictions.
 * Features multiple layouts, dark mode, and progress indicators.
 */
export function FileUpload({
  question,
  value,
  onChange,
  disabled,
  errors,
}: QuestionComponentProps<FileUploadAnswer[] | null>) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Extract config with defaults
  const config = question.config as FileUploadDisplayConfig | undefined;
  const allowedTypes = config?.allowedTypes || ['*/*'];
  const maxFileSizeMb = config?.maxFileSizeMb || 10;
  const maxFiles = config?.maxFiles || 1;
  const minFiles = config?.minFiles || 0;
  const files = value || [];

  // Display settings
  const layout = config?.uploadLayout || 'dropzone';
  const dropzoneText = config?.dropzoneText || 'Drop files here or click to upload';
  const buttonText = config?.buttonText || 'Choose File';
  const showPreview = config?.showPreview !== false;
  const previewSize = config?.previewSize || 'medium';
  const showFileSize = config?.showFileSize !== false;
  const showFileType = config?.showFileType !== false;
  const showProgress = config?.showProgress !== false;
  const showFileCount = config?.showFileCount !== false;
  const theme = config?.uploadTheme || 'default';

  // Convert allowed types to accept string
  const acceptString = allowedTypes.join(',');

  const validateFile = (file: File): string | null => {
    // Check file size
    const fileSizeMb = file.size / (1024 * 1024);
    if (fileSizeMb > maxFileSizeMb) {
      return `File "${file.name}" exceeds maximum size of ${maxFileSizeMb}MB`;
    }

    // Check file type
    if (!allowedTypes.includes('*/*')) {
      const isAllowed = allowedTypes.some((type) => {
        if (type.endsWith('/*')) {
          // Wildcard type like "image/*"
          const category = type.split('/')[0];
          return file.type.startsWith(`${category}/`);
        }
        return file.type === type || file.name.endsWith(type.replace('*', ''));
      });

      if (!isAllowed) {
        return `File type "${file.type}" is not allowed`;
      }
    }

    return null;
  };

  const uploadFile = async (file: File): Promise<FileUploadAnswer | null> => {
    const error = validateFile(file);
    if (error) {
      setUploadErrors((prev) => [...prev, error]);
      return null;
    }

    // Simulate upload progress (in real app, this would be actual upload)
    const fileId = `${file.name}-${Date.now()}`;

    try {
      // Simulate upload progress
      for (let progress = 0; progress <= 100; progress += 10) {
        await new Promise((resolve) => setTimeout(resolve, 50));
        setUploadProgress((prev) => ({ ...prev, [fileId]: progress }));
      }

      // In a real implementation, upload to server and get URL
      // For now, create a local object URL
      const fileUrl = URL.createObjectURL(file);

      return {
        fileName: file.name,
        fileUrl,
        fileSize: file.size,
        mimeType: file.type,
      };
    } finally {
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[fileId];
        return newProgress;
      });
    }
  };

  const handleFiles = useCallback(
    async (fileList: FileList) => {
      if (disabled) return;

      setUploadErrors([]);

      // Check if adding files would exceed max
      const filesToAdd = Array.from(fileList);
      const remainingSlots = maxFiles - files.length;

      if (filesToAdd.length > remainingSlots) {
        setUploadErrors([`You can only upload ${maxFiles} file(s). ${remainingSlots} slot(s) remaining.`]);
        filesToAdd.splice(remainingSlots);
      }

      // Upload files
      const uploadedFiles: FileUploadAnswer[] = [];
      for (const file of filesToAdd) {
        const result = await uploadFile(file);
        if (result) {
          uploadedFiles.push(result);
        }
      }

      if (uploadedFiles.length > 0) {
        onChange([...files, ...uploadedFiles]);
      }
    },
    [disabled, files, maxFiles, onChange]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
    // Reset input to allow re-uploading same file
    e.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    if (disabled) return;
    const newFiles = [...files];
    // Revoke object URL to prevent memory leaks
    if (newFiles[index].fileUrl.startsWith('blob:')) {
      URL.revokeObjectURL(newFiles[index].fileUrl);
    }
    newFiles.splice(index, 1);
    onChange(newFiles.length > 0 ? newFiles : null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return (
        <svg className="h-8 w-8 text-blue-500 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    }
    if (mimeType === 'application/pdf') {
      return (
        <svg className="h-8 w-8 text-red-500 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    }
    if (mimeType.startsWith('video/')) {
      return (
        <svg className="h-8 w-8 text-purple-500 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      );
    }
    if (mimeType.startsWith('audio/')) {
      return (
        <svg className="h-8 w-8 text-green-500 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
        </svg>
      );
    }
    return (
      <svg className="h-8 w-8 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  };

  const isUploadDisabled = disabled || files.length >= maxFiles;

  // Get theme classes
  const getDropzoneClasses = () => {
    const base = 'relative flex flex-col items-center justify-center rounded-lg transition-colors';

    switch (theme) {
      case 'minimal':
        return cn(base, 'border border-dashed p-6');
      case 'bordered':
        return cn(base, 'border-2 rounded-xl p-8');
      default:
        return cn(base, 'border-2 border-dashed p-8');
    }
  };

  return (
    <QuestionWrapper question={question} errors={errors}>
      <div className="space-y-4">
        {/* File count info */}
        {showFileCount && maxFiles > 1 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {files.length} of {maxFiles} files uploaded
            {minFiles > 0 && ` (minimum ${minFiles} required)`}
          </p>
        )}

        {/* Dropzone Layout */}
        {layout === 'dropzone' && (
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => !isUploadDisabled && fileInputRef.current?.click()}
            className={cn(
              getDropzoneClasses(),
              isDragOver && !isUploadDisabled
                ? 'border-primary-400 bg-primary-50 dark:bg-primary-950/30'
                : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50',
              isUploadDisabled
                ? 'cursor-not-allowed opacity-50'
                : 'cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
            )}
          >
            <svg
              className="mb-3 h-10 w-10 text-gray-400 dark:text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>

            <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
              {isDragOver ? 'Drop files here' : dropzoneText}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {allowedTypes.includes('*/*')
                ? `Any file type, max ${maxFileSizeMb}MB`
                : `Max ${maxFileSizeMb}MB per file`}
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept={acceptString}
              multiple={maxFiles > 1}
              onChange={handleInputChange}
              disabled={isUploadDisabled}
              className="hidden"
            />
          </div>
        )}

        {/* Button Layout */}
        {layout === 'button' && (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => !isUploadDisabled && fileInputRef.current?.click()}
              disabled={isUploadDisabled}
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                'bg-primary-500 text-white hover:bg-primary-600',
                'dark:bg-primary-600 dark:hover:bg-primary-700',
                isUploadDisabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {buttonText}
            </button>
            {files.length === 0 && (
              <span className="text-sm text-gray-500 dark:text-gray-400">No file chosen</span>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptString}
              multiple={maxFiles > 1}
              onChange={handleInputChange}
              disabled={isUploadDisabled}
              className="hidden"
            />
          </div>
        )}

        {/* Inline Layout */}
        {(layout === 'inline' || layout === 'compact') && (
          <div
            onClick={() => !isUploadDisabled && fileInputRef.current?.click()}
            className={cn(
              'flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer',
              'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50',
              'hover:border-gray-300 dark:hover:border-gray-600',
              isUploadDisabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <svg className="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <span className="flex-1 text-sm text-gray-600 dark:text-gray-300 truncate">
              {files.length > 0 ? `${files.length} file(s) selected` : dropzoneText}
            </span>
            <span className="text-sm font-medium text-primary-500 dark:text-primary-400">Browse</span>
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptString}
              multiple={maxFiles > 1}
              onChange={handleInputChange}
              disabled={isUploadDisabled}
              className="hidden"
            />
          </div>
        )}

        {/* Upload progress */}
        {showProgress && Object.keys(uploadProgress).length > 0 && (
          <div className="space-y-2">
            {Object.entries(uploadProgress).map(([fileId, progress]) => (
              <div key={fileId} className="flex items-center gap-3 rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                <svg className="h-5 w-5 animate-spin text-primary-500 dark:text-primary-400" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <div className="flex-1">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                    <div
                      className="h-full rounded-full bg-primary-500 dark:bg-primary-400 transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-300">{progress}%</span>
              </div>
            ))}
          </div>
        )}

        {/* Upload errors */}
        {uploadErrors.length > 0 && (
          <div className="rounded-lg bg-red-50 dark:bg-red-950/30 p-3 border border-red-200 dark:border-red-800">
            {uploadErrors.map((error, index) => (
              <p key={index} className="text-sm text-red-600 dark:text-red-400">
                {error}
              </p>
            ))}
          </div>
        )}

        {/* Uploaded files list */}
        {files.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Uploaded files:</p>
            {files.map((file, index) => (
              <div
                key={index}
                className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3"
              >
                {/* File icon or preview */}
                {showPreview && file.mimeType.startsWith('image/') ? (
                  <img
                    src={file.fileUrl}
                    alt={file.fileName}
                    className={cn('rounded object-cover', PREVIEW_SIZE_VALUES[previewSize])}
                  />
                ) : (
                  <div className={cn('flex items-center justify-center', PREVIEW_SIZE_VALUES[previewSize])}>
                    {getFileIcon(file.mimeType)}
                  </div>
                )}

                {/* File info */}
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{file.fileName}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    {showFileSize && <span>{formatFileSize(file.fileSize)}</span>}
                    {showFileSize && showFileType && <span>&middot;</span>}
                    {showFileType && <span>{file.mimeType.split('/')[1]?.toUpperCase() || file.mimeType}</span>}
                  </div>
                </div>

                {/* Remove button */}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className="rounded p-1.5 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                    aria-label="Remove file"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Allowed types hint */}
        {!allowedTypes.includes('*/*') && files.length === 0 && (
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Allowed: {allowedTypes.map(t => {
              if (t.endsWith('/*')) return t.split('/')[0];
              return t.split('/')[1];
            }).join(', ')}
          </p>
        )}
      </div>
    </QuestionWrapper>
  );
}
